import { ChevronDown, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

import { useListSessions } from '../../api/useListSessions';
import type { SessionDto } from '../../schemas/sessionSchemas';
import { displayApplicantName } from '../../sessionReference';
import { InitialAvatar, StatusIconBadge } from './sessionStatus';

const dateFmt = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

const FILTERS = ['All', 'PendingApproval', 'Completed', 'InProgress', 'Rejected'] as const;

// Every display status, sent as an explicit set so "All" means all. The API takes a
// comma-separated list of SessionOriginalStatus names and ignores ones it does not
// recognise, so this degrades to a narrower list rather than an empty one if the enum
// is ever trimmed.
const EVERY_STATUS = [
  'NotStarted',
  'InProgress',
  'Locked',
  'Expired',
  'InReview',
  'Approved',
  'Rejected',
].join(',');
const FILTER_LABEL: Record<string, string> = {
  All: 'All',
  PendingApproval: 'Pending',
  Completed: 'Completed',
  InProgress: 'In Progress',
  Rejected: 'Rejected',
};

// Left column — the full session list. Clicking a card opens that session in the
// detail view (the parent tracks open tabs + the active session). Mirrors the
// designer's SessionSidebar (search + status filter + scrollable card list).
export function SessionListPanel({
  selectedId,
  onSelect,
}: {
  selectedId?: string;
  onSelect: (session: SessionDto) => void;
}) {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<string>('All');
  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);

  // Pull a generous page — the detail view's list is a navigator, not a paginated table.
  //
  // "All" asks for every status EXPLICITLY rather than sending no status at all, because
  // those two no longer mean the same thing: an unfiltered list is the sessions table's
  // default view, which hides Not Started runs so they cannot bury the runs an admin has
  // to act on. That is right for a table and wrong for a navigator — you can open a Not
  // Started session from the table's Not Started filter, and the navigator must still be
  // able to list the session you currently have open.
  const { data, isLoading } = useListSessions({
    page: 1,
    pageSize: 100,
    status: filter === 'All' ? EVERY_STATUS : filter,
    // Asking for Not Started would otherwise pull in emailed invites too, and an invite
    // row has no session behind it to open.
    includeInvites: false,
  });

  // Close the filter dropdown on an outside click.
  useEffect(() => {
    if (!filterOpen) return;
    function onDocClick(e: MouseEvent) {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setFilterOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [filterOpen]);

  const sessions = useMemo(() => {
    const items = data?.items ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return items;
    // Search what the rows SHOW: the verified name, the mobile number, the workflow.
    // It used to match `applicantName`, so typing "app" matched every public run (they are
    // all labelled "Applicant") while typing the name actually on screen matched nothing.
    return items.filter((s) =>
      [s.applicantFullName, s.applicantMobile, s.workflowName].some((field) =>
        field?.toLowerCase().includes(q),
      ),
    );
  }, [data?.items, search]);

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-xl border border-[#E4E4E4] bg-white">
      {/* Controls */}
      <div className="flex shrink-0 items-center gap-2.5 border-b border-[#E4E4E4] p-[clamp(12px,0.9vw,14px)]">
        <div className="relative flex-1">
          <Search
            size={15}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search"
            className="h-[30px] w-full rounded-lg border border-[#D1D5DB] pl-9 pr-3 text-[length:var(--text-14)] text-[#111827] outline-none placeholder:text-[#9CA3AF] focus:border-primary focus:ring-1 focus:ring-primary/20"
          />
        </div>
        <div ref={filterRef} className="relative">
          <button
            type="button"
            onClick={() => setFilterOpen((v) => !v)}
            className={cn(
              'flex h-[30px] items-center gap-1.5 rounded-lg border px-3 text-[length:var(--text-14)] font-medium transition-colors',
              filter !== 'All'
                ? 'border-primary bg-[#EFF6FF] text-primary'
                : 'border-[#D1D5DB] text-[#4B5563] hover:bg-slate-50',
            )}
          >
            {FILTER_LABEL[filter]}
            <ChevronDown
              size={14}
              className={cn('transition-transform', filterOpen && 'rotate-180')}
            />
          </button>
          {filterOpen && (
            <div className="absolute right-0 z-50 mt-1 w-36 rounded-lg border border-[#E5E7EB] bg-white py-1 shadow-lg">
              {FILTERS.map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => {
                    setFilter(f);
                    setFilterOpen(false);
                  }}
                  className={cn(
                    'block w-full px-3 py-1.5 text-left text-[length:var(--text-14)] transition-colors hover:bg-[#F3F4F6]',
                    filter === f ? 'bg-[#EFF6FF] font-semibold text-primary' : 'text-[#374151]',
                  )}
                >
                  {FILTER_LABEL[f]}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* List */}
      <div className="custom-scrollbar flex min-h-0 flex-1 flex-col overflow-y-auto">
        {isLoading ? (
          <p className="p-4 text-[length:var(--text-14)] text-[#9CA3AF]">Loading…</p>
        ) : sessions.length === 0 ? (
          <p className="p-4 text-[length:var(--text-14)] text-[#9CA3AF]">No sessions found.</p>
        ) : (
          sessions.map((s) => {
            const selected = s.id === selectedId;
            // The verified name, or NA — never the "Applicant" placeholder, which made
            // every public run in this list look like the same person.
            const name = displayApplicantName(s.applicantFullName);
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => onSelect(s)}
                className={cn(
                  'flex flex-col gap-[clamp(10px,1vw,14px)] border-b border-[#E4E4E4] p-[clamp(12px,1vw,16px)] text-left transition-colors last:border-b-0',
                  selected ? 'bg-[#E5EDFF] hover:bg-[#EFF6FF]' : 'bg-white hover:bg-[#F9FAFB]',
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="flex min-w-0 items-center gap-2">
                    <InitialAvatar name={name} />
                    <span className="truncate text-[length:var(--text-16)] font-medium text-[#111827]">
                      {name}
                    </span>
                  </span>
                  {/* Icon only, name on hover — the designer's session card. The text pill
                      that was here fought the applicant's name for the same narrow row. */}
                  <StatusIconBadge status={s.status} />
                </div>
                <div className="flex flex-col gap-0.5">
                  <span className="truncate text-[length:var(--text-14)] text-[#666666]">
                    {s.workflowName}
                  </span>
                  <span className="text-[length:var(--text-12)] text-[#666666]">
                    {dateFmt.format(new Date(s.createdAt))}
                  </span>
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
